import { GroupeFormData } from "@repo/types";
import { Badge } from "@repo/ui/components/ui/badge";
import { Button } from "@repo/ui/components/ui/button";
import { Input } from "@repo/ui/components/ui/input";
import { Label } from "@repo/ui/components/ui/label";
import type { SubscriptionConfig } from "@repo/ui/lib/api/services/settings.service";
import { Check, Loader2 } from "lucide-react";

interface GroupeAdminSectionProps {
  formData: GroupeFormData;
  subscriptionConfig?: SubscriptionConfig;
  selectedPlanType: 'BASIC' | 'ADVANCED';
  selectedTierId: string;
  groupeId?: number;
  isUpdating: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubscriptionUpdate: () => Promise<void>;
}

export function GroupeAdminSection({
  formData,
  subscriptionConfig,
  selectedPlanType,
  selectedTierId,
  groupeId,
  isUpdating,
  handleInputChange,
  handleSubscriptionUpdate
}: GroupeAdminSectionProps) {
  return (
    <div className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <p className="text-sm text-orange-800 font-medium mb-1">⚠️ Zone administrateur</p>
        <p className="text-xs text-orange-700">
          Ces paramètres affectent directement l'abonnement du groupe
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">Plan sélectionné</Label>
          <div className="p-4 bg-muted rounded-lg border">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-semibold">
                {(formData as any).plan_type || 'BASIC'}
              </Badge>
              {selectedTierId && subscriptionConfig && (
                <span className="text-sm text-muted-foreground">
                  • {subscriptionConfig.plans[selectedPlanType]?.tiers?.find(t => t.id === selectedTierId)?.maxTrees || 0} parcours
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Modifiez via l'onglet "Abonnement"
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="max_parcours_per_month" className="text-sm font-medium">
            Max parcours / mois
            <Badge variant="destructive" className="ml-2 text-xs">Modifiable</Badge>
          </Label>
          <Input
            id="max_parcours_per_month"
            name="max_parcours_per_month"
            type="number"
            value={(formData as any).max_parcours_per_month || ''}
            onChange={handleInputChange}
            placeholder="Ex: 100"
            className="h-11 border-orange-300 focus:border-orange-500"
          />
          <p className="text-xs text-muted-foreground">
            Limite mensuelle unifiée (arbres + alignements + espaces boisés confondus)
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Parcours utilisés ce mois
          </Label>
          <div className="flex items-center gap-2 p-3 bg-muted rounded-md border h-11">
            <span className="text-sm font-semibold">
              {(formData as any).current_month_parcours ?? 0}
            </span>
            <span className="text-xs text-muted-foreground">
              / {(formData as any).max_parcours_per_month ?? '—'} parcours
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            Compteur lu depuis la base — remise à zéro automatique chaque mois
          </p>
        </div>
      </div>

      {groupeId && (
        <div className="pt-4 border-t">
          <Button
            type="button"
            onClick={handleSubscriptionUpdate}
            disabled={isUpdating}
            className="w-full bg-orange-600 hover:bg-orange-700 h-11"
          >
            {isUpdating ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Mise à jour...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4" />
                Mettre à jour l'abonnement
              </div>
            )}
          </Button>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Met à jour uniquement les paramètres d'abonnement
          </p>
        </div>
      )}
    </div>
  );
}
